import { baseApi } from '@alltrails/redux-helpers/baseApi';
import type {
  AreaHit,
  AreaSearchResult,
  AreaType,
  PhotospotHit,
  PlaceSearchResult,
  POIHit,
  POISearchResult,
  TrailHit,
  TrailSearchResult
} from '@alltrails/types/AlgoliaResults';
import type { LocaleUnits } from '@alltrails/types/LocaleUnits';
import ExploreServicePayload, { ExploreServiceResultType } from '../types/ExploreServicePayload';
import ExploreServiceResponse, { type ExploreServiceSearchResult } from '../types/ExploreServiceResponse';
import type { POIDetailsResponse } from '../types/POIDetailsResponse';
import cleanExploreServicePayloadLocation from '../utils/cleanExploreServicePayloadLocation';

export const url = '/api/alltrails/explore/v1/search';

const poiAttributesToRetrieve: (keyof POIHit)[] = [
  'ID',
  'location_label',
  'location_type',
  'name',
  'photos_count',
  'slug',
  'subtype',
  'popularity',
  'state_name',
  'trails_count',
  'type_label',
  '_geoloc'
];

const photospotAttributesToRetrieve: (keyof PhotospotHit)[] = [
  'ID',
  'objectID',
  'location_label',
  'location_type',
  'name',
  'popularity',
  'slug',
  'type_label',
  'subtype',
  '_geoloc',
  'photos_count',
  'trails_count',
  'state_name'
];

export const trailAttributesToRetrieve: (keyof TrailHit)[] = [
  'ID',
  'area_name',
  'avg_rating',
  'area_slug',
  'city_url',
  'difficulty_rating',
  'description',
  'duration_minutes',
  'duration_minutes_cycling',
  'duration_minutes_hiking',
  'duration_minutes_mountain_biking',
  'duration_minutes_trail_running',
  'estimated_time_to_complete',
  'has_profile_photo',
  'is_closed',
  'length',
  'location_label',
  'name',
  'num_reviews',
  'num_photos',
  'popularity',
  'primary_trailhead_id',
  'profile_photo_url',
  'secondary_trailhead_ids',
  'slug',
  '_geoloc',
  '_cluster_geoloc'
];

export const areaAttributesToRetrieve: (keyof AreaHit)[] = [
  'ID',
  '_geoloc',
  'area_type',
  'location_label',
  'name',
  'popularity',
  'slug',
  'trails_count',
  'type',
  'type_label'
];

export const exploreServiceApi = baseApi
  .enhanceEndpoints({ addTagTypes: ['List-Trails', 'Places', 'POI-Trails', 'Park-Trails', 'Trails'] })
  .injectEndpoints({
    endpoints: builder => ({
      getAreaTrails: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults'>,
        Pick<ExploreServicePayload, 'location'> & { localeUnits: LocaleUnits }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['boundingBox', 'searchResults']
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        }
      }),
      getListTrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'limit' | 'location' | 'sort'> & {
          additionalAttributesToRetrieve?: (keyof TrailHit)[];
          localeUnits: LocaleUnits;
        }
      >({
        query: ({ additionalAttributesToRetrieve, location, localeUnits, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: [...trailAttributesToRetrieve, ...(additionalAttributesToRetrieve || [])],
            recordTypesToReturn: ['trail'],
            resultsToInclude: ['searchResults', 'summary']
          };
          return {
            url,
            data,
            method: 'post',
            config: { headers: { 'X-Locale-Units': localeUnits } }
          };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['List-Trails']
      }),
      getLocationData: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults'>,
        Pick<ExploreServicePayload, 'filters' | 'limit' | 'location' | 'page' | 'recordAttributesToRetrieve' | 'sort'> & { localeUnits: LocaleUnits }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['boundingBox', 'searchResults']
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        }
      }),
      getParks: builder.mutation<
        Pick<ExploreServiceResponse<AreaSearchResult>, 'searchResults'>,
        Pick<ExploreServicePayload, 'limit' | 'location'> & { areaTypes?: AreaType[] }
      >({
        query: ({ areaTypes, limit, location }) => {
          const data: ExploreServicePayload = {
            filters: {
              collections: ['parks'],
              ...(areaTypes && { areaType: areaTypes })
            },
            ...(limit && { limit }),
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['searchResults']
          };
          return { url, data, method: 'post' };
        }
      }),
      getPlaces: builder.query<Pick<ExploreServiceResponse<PlaceSearchResult>, 'searchResults'>, Pick<ExploreServicePayload, 'location' | 'limit'>>({
        query: ({ location, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordTypesToReturn: ['area', 'city', 'country', 'state'],
            resultsToInclude: ['searchResults'],
            sort: 'most_popular'
          };
          return { url, data, method: 'post' };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Places']
      }),
      getPOITrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult | POISearchResult>, 'place' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'> & { localeUnits: LocaleUnits }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const attributes: string[] = [...poiAttributesToRetrieve, ...trailAttributesToRetrieve, 'polyline'];
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            recordTypesToReturn: ['poi', 'trail'],
            resultsToInclude: ['place', 'searchResults', 'summary']
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['POI-Trails']
      }),
      getParkTrails: builder.query<
        Pick<ExploreServiceResponse<TrailSearchResult | POISearchResult>, 'place' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'sort'> & { localeUnits: LocaleUnits }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const attributes: string[] = [...poiAttributesToRetrieve, ...trailAttributesToRetrieve, 'polyline'];
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            recordTypesToReturn: ['poi', 'trail'],
            resultsToInclude: ['place', 'searchResults', 'summary']
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Park-Trails']
      }),
      getResults: builder.query<
        Pick<ExploreServiceResponse<ExploreServiceSearchResult>, 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'filters' | 'location' | 'recordTypeLimits' | 'recordTypesToReturn' | 'sort'> & {
          localeUnits: LocaleUnits;
        }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const attributes: string[] = [];
          if (payload.recordTypesToReturn?.includes('poi')) {
            attributes.push(...poiAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('trail')) {
            attributes.push(...trailAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('photospot')) {
            attributes.push(...photospotAttributesToRetrieve);
          }
          if (payload.recordTypesToReturn?.includes('area')) {
            attributes.push(...areaAttributesToRetrieve);
          }
          const resultsToInclude: ExploreServiceResultType[] = ['searchResults', 'summary'];
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            recordAttributesToRetrieve: Array.from(new Set(attributes)),
            resultsToInclude
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        },
        keepUnusedDataFor: 3, // seconds
        providesTags: ['Trails']
      }),
      getResultsBoundingBox: builder.mutation<
        Pick<ExploreServiceResponse<TrailSearchResult>, 'boundingBox' | 'searchResults' | 'summary'>,
        Pick<ExploreServicePayload, 'expandSearch' | 'filters' | 'location' | 'sort'> & { localeUnits: LocaleUnits }
      >({
        query: ({ location, localeUnits, ...payload }) => {
          const data: ExploreServicePayload = {
            ...payload,
            location: cleanExploreServicePayloadLocation(location),
            resultsToInclude: ['searchResults', 'boundingBox', 'summary']
          };
          return { url, data, method: 'post', config: { headers: { 'X-Locale-Units': localeUnits } } };
        },
        invalidatesTags: ['Trails'],
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          const { ...rest } = args;
          const { data } = await queryFulfilled;
          rest.location = { ...data.boundingBox, mapRotation: 0 };
          dispatch(exploreServiceApi.util.upsertQueryData('getResults', rest, data));
        }
      }),
      getPOIDetails: builder.query<POIDetailsResponse, string>({
        query: slug => ({
          url: `/api/alltrails/points_of_interest/${slug}`,
          method: 'get'
        })
      })
    })
  });

export const {
  useGetAreaTrailsMutation,
  useGetListTrailsQuery,
  useGetLocationDataMutation,
  useGetParksMutation,
  useGetPOITrailsQuery,
  useGetParkTrailsQuery,
  useGetPOIDetailsQuery,
  useGetResultsQuery,
  useGetResultsBoundingBoxMutation,
  useLazyGetListTrailsQuery,
  useLazyGetPlacesQuery,
  useLazyGetPOIDetailsQuery
} = exploreServiceApi;
